import { apiV1 } from "api-v2";
import type { AxiosResponse } from "axios";

import type {
	Company,
	CompanyLocationFormValue,
	CorporateDocument,
	GlobalPresenceOption,
	LGA,
	PastProject,
} from "newui/features/Company/types";
import type { KeyPersonnel } from "newui/features/SupplierSearch/types/supplier-search.types";
import type { KeyPersonnelDetail } from "newui/features/Company/KeyPersonnel/types/KeyPersonnel.type";
import type { KeyPersonnelProfileView } from "newui/features/Company/types/KeyPersonnelProfileView.type";
import type { LabelValue } from "newui/types/LabelValue.type";
import { processFormData } from "utils/helpers";
import { CompanySizeFetchParams } from "./types/CompanyInformationForm.type";

export const getCompanyProfile = (
	companyId: number,
	withPastProjects = true
): Promise<AxiosResponse<Company>> => {
	const baseUrl = `companies/${companyId}`;
	const url = withPastProjects
		? baseUrl
		: `${baseUrl}?past_projects_published=false`;

	return apiV1.get<Company>(url);
};

export const getCompanyAccountDetails = (
	companyId: number
): Promise<AxiosResponse<CompanyAccountDetails>> => {
	return apiV1.get<CompanyAccountDetails>(
		`companies/${companyId}/account-information`
	);
};

export const updateCompanyInformation = (
	companyId: number,
	data: Record<string, any>
): Promise<AxiosResponse<Company>> => {
	const formData = processFormData(data);
	return apiV1.patch<Company>(`companies/${companyId}`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
};

export const fetchCompanySizes = async (
	fetchParams: CompanySizeFetchParams
): Promise<LabelValue<number>[]> => {
	const { data } = await apiV1.get(
		`companies/sizes/${fetchParams.profileType ?? 1}`
	);
	const items = data.data ?? data;

	if (!Array.isArray(items)) {
		return [];
	}

	// TODO: Need to update database values so do not need to do this
	const allOptionIndex = items.findIndex(
		(item) => item.name.toLowerCase() === "all of above"
	);

	const returnValues = items.map((s: any, index: number) => ({
		label: index === allOptionIndex ? "Show All" : s.name,
		value: s.id,
	}));

	if (fetchParams.showAllOption) {
		return returnValues;
	}

	return returnValues.filter((_, index) => index !== allOptionIndex);
};

export const fetchGlobalPresence = async (): Promise<
	GlobalPresenceOption[]
> => {
	const { data } = await apiV1.get("global-presence");
	const items = data.data ?? data;
	return Array.isArray(items) ? items : [];
};

export const fetchSpecialities = async (): Promise<LabelValue<number>[]> => {
	const { data } = await apiV1.get("speciality");
	const items = data.data ?? data;
	return Array.isArray(items)
		? items.map((s: any) => ({ label: s.name, value: s.id }))
		: [];
};

export const fetchTrades = async (): Promise<LabelValue<number>[]> => {
	const { data } = await apiV1.get("trades?page_size=9999");
	const items = data.data ?? data;
	return Array.isArray(items)
		? items.map((t: any) => ({ label: t.name, value: t.id }))
		: [];
};

export const fetchLGAs = async (): Promise<LGA[]> => {
	const { data } = await apiV1.get("companies/local-govt-associations");
	const items = data.data ?? data;
	return Array.isArray(items) ? items : [];
};

export const updateCompanyLocations = (
	companyId: number,
	locations: CompanyLocationFormValue[]
): Promise<AxiosResponse<Company>> => {
	return apiV1.patch<Company>(`companies/${companyId}/update-locations`, {
		locations,
	});
};

export const deleteCompanyAddress = (
	companyId: number,
	addressId: number
): Promise<AxiosResponse<void>> => {
	return apiV1.delete<void>(
		`companies/${companyId}/delete-address/${addressId}`
	);
};

/* ---- Past Projects ---- */

const unwrap = <T>(responseData: unknown): T => {
	if (
		responseData &&
		typeof responseData === "object" &&
		"data" in responseData
	) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export const getCompanyPastProjects = async (
	companyId: number
): Promise<PastProject[]> => {
	const response = await apiV1.get(
		`past-projects?filter[company_id]=${companyId}`
	);
	return unwrap<PastProject[]>(response.data);
};

/* ---- Key Personnel ---- */

export const getCompanyKeyPersonnel = async (
	companyId: number
): Promise<KeyPersonnel[]> => {
	const response = await apiV1.get(`companies/${companyId}/key-personnel`);
	return unwrap<KeyPersonnel[]>(response.data);
};

export const getKeyPersonnelDetail = async (
	keyPersonnelId: number
): Promise<KeyPersonnelDetail> => {
	const response = await apiV1.get(`companies/key-personnel/${keyPersonnelId}`);
	const data = response.data;
	return data?.data?.key_personnel ?? data?.key_personnel ?? data;
};

/* ---- Profile View ---- */

export const getKeyPersonnelProfileView = async (
	companyId: number
): Promise<KeyPersonnelProfileView> => {
	const response = await apiV1.get(
		`companies/${companyId}/profile-view/key-personnel`
	);
	return unwrap<KeyPersonnelProfileView>(response.data);
};

/* ---- Documents ---- */

export interface DocumentCategory {
	id: number;
	name: string;
	parent_id: number;
	exclude_from_profile_id: number;
	created_at: string;
	updated_at: string;
}

export const getDocumentCategories = async (
	profileTypeId: number
): Promise<DocumentCategory[]> => {
	const response = await apiV1.get(
		`document-categories?filter[exclude_from_profile_id]=${profileTypeId}`
	);
	return unwrap<DocumentCategory[]>(response.data);
};

export const getCompanyDocuments = async (
	companyId: number,
	includeDrafts = false
): Promise<CorporateDocument[]> => {
	const params = new URLSearchParams({
		"filter[documentable_id]": String(companyId),
		"filter[documentable_type]": "App\\Models\\Company",
	});
	if (includeDrafts) {
		params.append("include_drafts", "true");
	}
	const response = await apiV1.get(`documents?${params.toString()}`);
	return unwrap<CorporateDocument[]>(response.data);
};

export const updateDocument = (
	companyId: number,
	documentId: number,
	formData: FormData
): Promise<AxiosResponse<{ data: CorporateDocument }>> => {
	return apiV1.post<{ data: CorporateDocument }>(
		`companies/${companyId}/documents/${documentId}`,
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data",
				"X-HTTP-Method-Override": "PATCH",
			},
		}
	);
};

export const updateDocumentDisplay = (
	companyId: number,
	documentId: number,
	display: "public" | "private"
): Promise<AxiosResponse<{ data: CorporateDocument }>> => {
	return apiV1.patch<{ data: CorporateDocument }>(
		`companies/${companyId}/documents/${documentId}`,
		{ display }
	);
};

export const deleteDocument = (
	companyId: number,
	documentId: number
): Promise<AxiosResponse<void>> => {
	return apiV1.delete<void>(`companies/${companyId}/documents/${documentId}`);
};

export const fetchDocumentAuditLogs = async (
	companyId: number,
	documentId: number
): Promise<import("./types/DocumentAuditLog.type").DocumentAuditLog[]> => {
	const { data } = await apiV1.get(
		`companies/${companyId}/documents/${documentId}/audit-logs`
	);
	return data.data;
};
